import React, { createContext, useContext, useEffect, useState } from 'react'
import { Auth, API, Hub } from 'aws-amplify'
import PropTypes from "prop-types";

// GraphQL
import {
  listUserPermissions,
  listUsers,
  getUserPermissionsCache,
  userPermissionsCacheByUserId
} from '../../graphql/queries'
import { graphqlOperation } from 'aws-amplify'

// initialize context with an empty object
const UserContext = createContext({})

export default function RDAuthContext({children}) {
  const [cognitoUser, setCognitoUser] = useState(null)
  const [user, setUser] = useState(null)
  const [permissions, setPermissions] = useState(null)

  useEffect(() => {
    console.log('initial useEffect -> checkUser()')
    checkUser()
  }, [])

  useEffect(() => {
    Hub.listen('auth', () => {
      //console.log('Hub.listen useEffect -> checkUser()')
      checkUser()
    })
  }, [])

  async function checkUser() {
    console.log('checkUser...')
    try {
      // Get the current authenticated user
      const user = await Auth.currentUserPoolUser()
      if(user) {
        setCognitoUser(user)
      }
    } catch (err) {
      console.log('no cognitoUser')
      // set the user to null if there is no user
      setCognitoUser(null)
    }
  }

  useEffect(() => {
    console.log('cognitoUser changed', cognitoUser)
    if(cognitoUser !== null){
      // getPermissions()
      getUserData()
    } else {
      setUser(null)
    }
  }, [cognitoUser])

  async function getUserData(){
    console.log('getUserData')    
    try {
      const users = []

      const filter = {
        userName: {
          eq: cognitoUser.username
        }
      }

      let nextToken

      do {
        const queryResponse = await API.graphql({
          query: listUsers,
          variables: {
            filter: filter,
            nextToken: nextToken
          }
        })
        console.log('queryResponse', queryResponse)
        users.push(...queryResponse.data.listUsers.items)
        nextToken = queryResponse.data.listUsers.nextToken
      } while (nextToken)
      setUser(users[0])
    } catch (err) {
      console.log('error fetching userData', err)
      setUser(null)
    }    
  }

  useEffect(() => {
    if(user !== null){
      getPermissions()
    }
  }, [user])

  async function getPermissions() {
    console.log('getPermissions()...')

    // Determine which permission system to use based on client
    // GRM uses legacy permissions, all other clients use new system
    const useNewPermissionSystem = user.client && user.client !== 'grm';

    console.log(`Client: ${user.client}, Using ${useNewPermissionSystem ? 'NEW' : 'LEGACY'} permission system`);

    if (useNewPermissionSystem) {
      // Try to fetch from UserPermissionsCache (new system)
      try {
        console.log('Attempting to fetch permissions from UserPermissionsCache...');

        // Query by userId to get cached permissions
        const cacheResponse = await API.graphql(
          graphqlOperation(userPermissionsCacheByUserId, {
            userId: user.id,
            limit: 1
          })
        );

        const cachedPermissions = cacheResponse.data.userPermissionsCacheByUserId?.items?.[0];

        if (cachedPermissions) {
          console.log('Found cached permissions, checking field types...');
          console.log('Field types received from AppSync:', {
            permissionsType: typeof cachedPermissions.permissions,
            navigationType: typeof cachedPermissions.navigation,
            dataScopeType: typeof cachedPermissions.dataScope,
            permissionsValue: cachedPermissions.permissions,
            navigationValue: cachedPermissions.navigation,
            dataScopeValue: cachedPermissions.dataScope
          });

          // Parse AWSJSON fields (permissions, navigation are strings)
          // dataScope is a structured type (already an object)
          const parsedPermissions = {
            ...cachedPermissions,
            permissions: cachedPermissions.permissions
              ? (typeof cachedPermissions.permissions === 'string'
                  ? JSON.parse(cachedPermissions.permissions)
                  : cachedPermissions.permissions)
              : {},
            navigation: cachedPermissions.navigation
              ? (typeof cachedPermissions.navigation === 'string'
                  ? JSON.parse(cachedPermissions.navigation)
                  : cachedPermissions.navigation)
              : {},
            dataScope: cachedPermissions.dataScope
              ? (typeof cachedPermissions.dataScope === 'string'
                  ? JSON.parse(cachedPermissions.dataScope)
                  : cachedPermissions.dataScope)
              : {}
          };

          console.log('Parsed permissions structure:', {
            hasNavigation: !!parsedPermissions.navigation,
            hasDataScope: !!parsedPermissions.dataScope,
            navigationKeys: Object.keys(parsedPermissions.navigation || {}),
            navigationVersion: parsedPermissions.navigation?.version,
            navigationSectionCount: parsedPermissions.navigation?.sections?.length,
            navigationHasError: !!parsedPermissions.navigation?.error,
            dataScopeKeys: Object.keys(parsedPermissions.dataScope || {}),
            dataScopeScopeType: parsedPermissions.dataScope?.scopeType
          });

          // Check for navigation errors
          if (parsedPermissions.navigation?.error) {
            console.error('Navigation generation error detected:', parsedPermissions.navigation.error);
            console.error('User will see minimal navigation with error indicator');
          }

          setPermissions(parsedPermissions);
          return;
        } else {
          console.warn('No cached permissions found for user:', user.id);
          console.warn('User may need permission recalculation. Please contact admin.');
          // TODO: Could trigger automatic recalculation here via Lambda
        }
      } catch (err) {
        console.error('Error fetching new permissions from cache:', err);
        console.log('Will fall back to legacy permissions if available');
      }
    }

    // Fallback to legacy permissions (for GRM or if new system fails)
    try {
      console.log('Fetching legacy permissions for role:', user.role);
      const permissionResponse = await API.graphql({
        query: listUserPermissions,
        variables: {
          role: user.role
        }
      })
      console.log('Legacy permissions loaded successfully');
      setPermissions(permissionResponse.data.listUserPermissions)
    } catch (err) {
      console.error('Error fetching legacy permissions:', err);
      console.log('user', user);
      setPermissions(null);
    }
  }

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        permissions,
      }}
    >
      { children }
    </UserContext.Provider>
  )
}

RDAuthContext.propTypes = {
  children: PropTypes.node
}

export const useUser = () => useContext(UserContext)